import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
// import { AxiosResponse } from "axios";
// import { DateTime } from "luxon";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { Add, CloudDownload, History, MoreVert } from "@mui/icons-material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import VersionHistoryModal from "../Modals/VersionHistoryModal";
import AddVersionModal from "../Modals/AddVersionModal";
import EditDocumentModal from "../Modals/EditDocumentModal";
import StatusBadge from "../../Common/UI/StatusTag";
import { DateTime } from "luxon";
import AddDocumentModal from "../Modals/AddDocumentModal";

type Props = {
  canEdit: boolean;
};

export const documentTypeOptions = [
  { label: "Protocol Documents", value: "Protocol Documents" },
  { label: "IRB document", value: "IRB document" },
  { label: "Agreements", value: "Agreements" },
];

export const documentStatusColor: any = {
  active: {
    variant: "active",
    label: "Active",
  },
  inactive: {
    variant: "cancelled",
    label: "Inactive",
  },
};

const Documents = ({ canEdit }: Props) => {
  const { id: studyId } = useParams();

  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState("");
  const [documentsData, setDocumentsData] = useState<any>([]);
  // const [documentsCount, setDocumentsCount] = useState(0);
  // const [page, setPage] = useState(0);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState("");
  const [toggleLoader, setToggleLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const modifySearchTerm = useMemo(
    () =>
      debounce((val: string) => {
        setSearchText(val);
      }, 500),
    [setSearchText]
  );

  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        setLoading(true);

        let url = `/study/${studyId}/documents`;
        if (searchText) {
          url += `?query=${searchText}`;
        }
        const res = await http.get(url);
        const data = res.data?.data;

        const formattedData = data?.map((item: any) => ({
          id: item?.id,
          documentName: item?.name || "",
          type: item?.type || "",
          category: item?.category || "",
          version: item?.versions?.[0]?.version || "",
          versionId: item?.versions?.[0]?.id || "",
          uploadDate: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("dd/MM/yyyy")
            : "",
          fileUrl: item?.filePath || item?.url,
          status: item?.status || "",
          hasMoreVersions: item?.versions?.[0]?.version > 1,
        }));

        setDocumentsData(formattedData || []);
        // setDocumentsCount(data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDocumentsData();
  }, [studyId, toggleLoader, searchText]);

  // const handleChangePage = (_1: any, newPage: number) => {
  //   setPage(newPage);
  // };

  const handleVersionHistory = async (document: any) => {
    setSelectedDocument({
      ...document,
    });
    setShowVersionModal(true);
  };

  const closeVersionModal = () => {
    setShowVersionModal(false);
    setSelectedDocument(null);
  };

  const closeAddVersionModal = () => {
    setShowAddVersionModal(false);
    setSelectedDocument(null);
  };

  const handleEditDocument = (document: any) => {
    setSelectedDocument({
      ...document,
    });
    setShowEditModal(true);
    handleMenuClose();
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedDocument(null);
  };

  const refreshDocumentsData = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleDownload = async (id: string, versionId: string) => {
    try {
      setDownloadLoading(id);
      let res = await http.get(
        `/study/${studyId}/documents/${id}/version/${versionId}`
      );

      const downloadLink = res?.data?.data?.previewUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadLoading("");
    } catch (err) {
      setDownloadLoading("");
      errorToastMessage(err as Error);
    }
  };

  const handleAddNewVersion = (row: any) => {
    setSelectedDocument({
      ...row,
    });
    setShowAddVersionModal(true);
    handleMenuClose();
  };

  const handleStatusChange = async (row: any) => {
    try {
      setMenuLoader("statusChange");
      const body = {
        status: row.status === "active" ? "inactive" : "active",
      };
      const res = await http.patch(
        `/study/${studyId}/documents/${row.id}/status`,
        body
      );
      toastMessage("success", res.data.message);
      setMenuLoader("");
      refreshDocumentsData();
      handleMenuClose();
    } catch (err) {
      errorToastMessage(err as Error);
      setMenuLoader("");
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <TextField
              placeholder="Search documents"
              sx={{ width: "30%" }}
              defaultValue={searchText}
              onChange={(e) => modifySearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {canEdit && (
              <Button
                variant="contained"
                onClick={() => setShowDocumentModal(true)}
                startIcon={<Add />}
              >
                Add Document
              </Button>
            )}
          </Box>
        }
        footer={
          <DataTableFooter
            filteredCount={documentsData.length}
            page={0}
            pageSize={documentsData.length || 1}
            onPageChange={() => {}}
            loading={loading}
            hidePagination
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Document Name</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell>Version</StyledTableCell>
              <StyledTableCell>Upload Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Download</StyledTableCell>
              <StyledTableCell align="right" />
            </TableRow>
          </TableHead>
          {!loading && documentsData?.length > 0 && (
            <TableBody>
              {documentsData?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                      }}
                    >
                      {row?.documentName || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {row?.type || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {row?.category || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "regular",
                          color: "text.primary",
                        }}
                      >
                        {row?.version || "-"}
                      </Typography>
                      {row?.hasMoreVersions && (
                        <IconButton
                          onClick={() => handleVersionHistory(row)}
                          sx={{
                            color: "primary.main",
                            padding: "4px",
                          }}
                        >
                          <History fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                      }}
                    >
                      {row?.uploadDate || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      label={
                        documentStatusColor[row?.status]?.label ?? row?.status
                      }
                      variant={documentStatusColor[row?.status]?.variant}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {downloadLoading === row?.id ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "54px",
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <IconButton
                        onClick={() => handleDownload(row.id, row.versionId)}
                        disabled={!!downloadLoading}
                      >
                        <CloudDownload />
                      </IconButton>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {canEdit && (
                      <IconButton onClick={(e) => handleMenuClick(e, row)}>
                        <MoreVert />
                      </IconButton>
                    )}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && documentsData.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No documents found
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      {showVersionModal && selectedDocument && (
        <VersionHistoryModal
          showModal={showVersionModal}
          closeModal={closeVersionModal}
          documentName={selectedDocument.documentName}
          documentType={selectedDocument.type}
          documentCategory={selectedDocument.category}
          studyId={studyId || ""}
          documentId={selectedDocument.id}
        />
      )}

      {showAddVersionModal && selectedDocument && (
        <AddVersionModal
          showModal={showAddVersionModal}
          closeModal={closeAddVersionModal}
          studyId={studyId || ""}
          documentId={selectedDocument.id}
          documentName={selectedDocument.documentName}
          documentCategory={selectedDocument.category}
          documentType={selectedDocument.type}
          onVersionAdded={refreshDocumentsData}
        />
      )}

      {showEditModal && selectedDocument && (
        <EditDocumentModal
          showModal={showEditModal}
          closeModal={closeEditModal}
          studyId={studyId || ""}
          documentId={selectedDocument.id}
          documentName={selectedDocument.documentName}
          documentCategory={selectedDocument.category}
          documentType={selectedDocument.type}
          onDocumentUpdated={refreshDocumentsData}
        />
      )}
      {showDocumentModal && (
        <AddDocumentModal
          showModal={showDocumentModal}
          closeModal={() => setShowDocumentModal(false)}
          studyId={studyId || ""}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        TransitionProps={{
          exit: false,
        }}
      >
        <MenuItem onClick={() => handleEditDocument(selectedRow)}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {selectedRow?.status === "active" && (
          <MenuItem onClick={() => handleAddNewVersion(selectedRow)}>
            <ListItemText>Add new version</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleStatusChange(selectedRow)}
          disabled={!!menuLoader}
        >
          {menuLoader === "statusChange" && (
            <ListItemIcon>
              <CircularProgress size={18} />
            </ListItemIcon>
          )}
          <ListItemText>
            {selectedRow?.status === "active" ? "De-activate" : "Activate"}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Documents;
